import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowDownToLine, PackageOpen } from "lucide-react";

import { toast } from "react-toastify";
import { useTranslation } from "../i18n";
import { playError, playSuccess } from "./helpers/audioPlayer";
import { useDemoVisible } from "./helpers/demoMode";
import { canonicalWarehouse } from "./helpers/warehouseAlias";
import HiddenScanInput from "./HiddenScanInput";
import ScanStatusIcons from "./ScanStatusIcons";
import ScanFlashOverlay from "./ScanFlashOverlay";
import FitWord from "./FitWord";
import { apiFetch } from "../api/apiFetch";
import { PasBayReadyTable, type PickAndSortSession, type PickAndSortBayReady } from "./zone-storage-pick-and-sort";
import "../styles/scansheet.css";
import "../styles/picking.css";

// The logged-in user's warehouse (full name, e.g. "Newport") — used only to
// pull demo Pick & Sort bay barcodes for this screen's demo panel.
const userWarehouse = (): string => {
    try {
        const raw = window.localStorage.getItem("user");
        return raw ? (JSON.parse(raw).warehouse || "") : "";
    } catch { return ""; }
};

const ZoneStorageSelectLocation = ({ set_userLocation, onPickAndSort }: {
    set_userLocation: React.Dispatch<React.SetStateAction<{ warehouse: string; sorter: string; bay: string; }>>;
    onPickAndSort?: (session: PickAndSortSession) => void;
}) => {

    const navigate = useNavigate();
    const { t } = useTranslation();
    const demo = useDemoVisible();
    const [barcodeScan, set_barcodeScan] = useState<string>("");
    const [scanStatus, set_scanStatus] = useState<"idle" | "success" | "error">("idle");
    const [demoBays, set_demoBays] = useState<string[]>([]);
    const statusTimerRef = useRef<NodeJS.Timeout | null>(null);

    // Pick & Sort tote-selects-wave step: a bay scan only ANCHORS the operator
    // to the bay (BE claims nothing); the wave is chosen by scanning one of its
    // source totes. pasBay holds the scanned bay-slot barcode while we wait.
    const [pasBay, set_pasBay] = useState<string | null>(null);
    // Which wall the anchored bay belongs to. A put-wall bay only ever pulls
    // put-wall waves and a P&S bay only Sorting waves (the BE never crosses
    // the queues), so the tote list is scoped to the scanned wall — showing
    // the other wall's waves would just offer totes that can't be scanned here.
    const [pasStation, set_pasStation] = useState<"pick_and_sort" | "put_wall">("pick_and_sort");
    const [pasWaves, set_pasWaves] = useState<{
        wave_id: number; completed_at: string | null; picker_name: string | null;
        order_count: number; item_count?: number; totes?: string[]; in_progress: boolean;
        sort_station?: "pick_and_sort" | "put_wall";
    }[]>([]);

    // While the tote step is showing, keep the queue fresh — another operator
    // finishing or claiming a wave changes what the totes on this list mean.
    useEffect(() => {
        if (!pasBay) return;
        const wh = userWarehouse();
        if (!wh) return;
        let alive = true;
        const load = async () => {
            try {
                const res = await apiFetch(`/pick-and-sort/waves?warehouse=${encodeURIComponent(wh)}&station=${pasStation}`, {});
                if (!res.ok || !alive) return;
                const data = await res.json();
                // The station filter is also applied here so an older BE that
                // ignores ?station= still shows only this wall's queue.
                const list: { sort_station?: string }[] = Array.isArray(data.waves) ? data.waves : [];
                if (alive) set_pasWaves(list.filter(w => (w.sort_station ?? "pick_and_sort") === pasStation) as typeof pasWaves);
            } catch { /* the step still works without the list */ }
        };
        load();
        const id = setInterval(load, 5000);
        return () => { alive = false; clearInterval(id); };
    }, [pasBay, pasStation]);

    // Pembroke is a Pick & Sort-only site: rather than the generic "scan a
    // location" prompt, its operators land straight on the loader/unloader
    // toggle. The wave is still resolved by scanning one of the bay's slot
    // locations (handled below, same as any other P&S entry) — the toggle just
    // surfaces the workflow up-front instead of behind a scan. canonicalWarehouse
    // folds the legacy "Pembroke Dock" name onto "Pembroke".
    const isPembroke = useMemo(() => canonicalWarehouse(userWarehouse()) === "Pembroke", []);
    const [view, set_view] = useState<"loading" | "unloading">("loading");

    // Pre-wave toggle badges: warehouse-wide Sorting counts — items still to
    // sort (loading side) and fully-sorted slots awaiting unload (unloading
    // side). Same numbers as the home tile, polled at the same 5s cadence, so
    // the operator can see there's work waiting before scanning into a wave.
    const [sortCount, set_sortCount] = useState<{ items: number; totes: number } | null>(null);
    useEffect(() => {
        if (!isPembroke) return;
        const wh = userWarehouse();
        if (!wh) return;
        let alive = true;
        const load = async () => {
            try {
                const res = await apiFetch(`/pick-and-sort/sort-count?warehouse=${encodeURIComponent(wh)}`, {});
                if (!res.ok || !alive) return;
                const data = await res.json();
                if (alive && typeof data.items === "number" && typeof data.totes === "number") {
                    set_sortCount({ items: data.items, totes: data.totes });
                }
            } catch { /* keep last-known counts */ }
        };
        load();
        const id = setInterval(load, 5000);
        return () => { alive = false; clearInterval(id); };
    }, [isPembroke]);

    // Unloading pill, pre-wave: the closer's per-bay "Ready to close" table.
    // The badge above says how many orders are waiting in the whole
    // warehouse; this says WHICH bay they're in, so the closer knows where
    // to walk before scanning anything. Polled only while that pill is
    // showing, at the same 5s cadence as the badges. Same component the
    // in-wave unloading view uses — no bay is "here" yet, so none is marked.
    const [bayReady, set_bayReady] = useState<PickAndSortBayReady[]>([]);
    const showBayReady = isPembroke && view === "unloading" && !pasBay;
    useEffect(() => {
        if (!showBayReady) return;
        const wh = userWarehouse();
        if (!wh) return;
        let alive = true;
        const load = async () => {
            try {
                const res = await apiFetch(`/pick-and-sort/bay-ready?warehouse=${encodeURIComponent(wh)}`, {});
                if (!res.ok || !alive) return;
                const data = await res.json();
                if (alive && Array.isArray(data.bays)) set_bayReady(data.bays);
            } catch { /* keep the last table */ }
        };
        load();
        const id = setInterval(load, 5000);
        return () => { alive = false; clearInterval(id); };
    }, [showBayReady]);
    const noBayHere = useMemo(() => new Set<number>(), []);

    // Demo: bay-location barcodes that can enter a Pick & Sort sort (resume or
    // start the next wave awaiting sort) for the user's warehouse.
    useEffect(() => {
        if (!demo || !onPickAndSort) return;
        const wh = userWarehouse();
        if (!wh) return;
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch(`/pick-and-sort/demo?warehouse=${encodeURIComponent(wh)}`, {});
                if (!res.ok || !alive) return;
                const data = await res.json();
                if (alive) set_demoBays(Array.isArray(data.bays) ? data.bays : []);
            } catch { /* hide panel */ }
        })();
        return () => { alive = false; };
    }, [demo, onPickAndSort]);

    const flashScanStatus = (status: "success" | "error") => {
        if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
        set_scanStatus(status);
        statusTimerRef.current = setTimeout(() => set_scanStatus("idle"), 500);
    };

    const warehouseTranslations: { [key: string]: string } = { "NP": "Newport", "PL": "Poland", "TT": "Test" };

    const validation: { warehouse: string[], sorter: string[], bay: { [key: string]: string[] } } =
    {
        warehouse: ["NP", "PL", "TT"],
        sorter: ["S1", "S2", "S3", "S4"],
        bay: { NP: ["99", "01", "02", "03", "04", "05", "06", "07", "08"], PL: ["99", "01", "02", "03", "04", "05", "06", "07", "08", "09"], TT: ["99", "01", "02", "03", "04"] }
    };

    // Does the scan parse as a valid 6-char WWSSBB Sorter 1/2 location?
    const parseSorterCode = (val: string): { longWarehouse: string; sorter: string; bay: string } | null => {
        if (val.length !== 6) return null;
        const scannedWarehouse = val.slice(0, 2);
        const scannedSorter = val.slice(2, 4);
        const scannedBay = val.slice(4, 6);
        if (!validation["warehouse"].includes(scannedWarehouse)) return null;
        if (!validation["sorter"].includes(scannedSorter)) return null;
        if (!validation["bay"][scannedWarehouse].includes(scannedBay)) return null;
        return { longWarehouse: warehouseTranslations[scannedWarehouse], sorter: scannedSorter.slice(-1), bay: scannedBay };
    };

    const handle_location_barcode_scan = useCallback(async (scannedValue?: string) => {
        const val = (scannedValue ?? barcodeScan).trim();
        set_barcodeScan("");
        if (!val) return;

        // Sorter 1/2 path — untouched.
        const sorterLoc = parseSorterCode(val);
        if (sorterLoc) {
            flashScanStatus("success");
            playSuccess();
            set_userLocation({ warehouse: sorterLoc.longWarehouse, sorter: sorterLoc.sorter, bay: sorterLoc.bay });
            return;
        }

        // Not a Sorter location — try resolving it as a Pick & Sort bay
        // location. With await_tote the BE claims nothing: it resumes a wave
        // already mid-sort at this bay, or answers awaiting_tote so the
        // operator picks the wave by scanning one of its source totes.
        if (onPickAndSort) {
            try {
                const res = await apiFetch(`/pick-and-sort/enter`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ barcode: val, await_tote: true }),
                });
                const data = await res.json();
                if (res.ok && data.ok && data.awaiting_tote) {
                    flashScanStatus("success");
                    playSuccess();
                    set_pasStation(data.sort_station === "put_wall" ? "put_wall" : "pick_and_sort");
                    set_pasBay(val);
                    return;
                }
                if (res.ok && data.ok) {
                    flashScanStatus("success");
                    playSuccess();
                    onPickAndSort(data as PickAndSortSession);
                    return;
                }
                if (res.ok && data.reason === "no_wave_awaiting_sort") {
                    flashScanStatus("error");
                    playError();
                    toast.dismiss(); toast.error(t("pas_no_wave_awaiting_sort"));
                    return;
                }
                if (res.ok && data.reason === "insufficient_locations") {
                    flashScanStatus("error");
                    playError();
                    toast.dismiss(); toast.error(t("pas_insufficient_locations"));
                    return;
                }
                if (res.ok && data.reason === "nothing_to_sort") {
                    // The wave had no picked items (all lines went missing) —
                    // the BE closed it. The next scan starts the next wave.
                    flashScanStatus("error");
                    playError();
                    toast.dismiss(); toast.error(t("pas_nothing_to_sort"));
                    return;
                }
                // Double-bay Large wave gating.
                if (res.ok && data.reason === "large_bays_not_empty") {
                    const bays = Array.isArray(data.occupied_bays) ? data.occupied_bays.join(", ") : "";
                    flashScanStatus("error");
                    playError();
                    toast.dismiss();
                    toast.error(`${t("pas_large_bays_not_empty")}${bays ? ` (${bays})` : ""}`);
                    return;
                }
                if (res.ok && data.reason === "large_wrong_bay") {
                    flashScanStatus("error");
                    playError();
                    toast.dismiss(); toast.error(t("pas_large_wrong_bay"));
                    return;
                }
                if (res.ok && data.reason === "large_bays_unavailable") {
                    flashScanStatus("error");
                    playError();
                    toast.dismiss(); toast.error(t("pas_large_bays_unavailable"));
                    return;
                }
            } catch { /* fall through to invalid barcode */ }
        }

        flashScanStatus("error");
        playError();
        toast.dismiss(); toast.error(t("invalid_barcode"));
    }, [barcodeScan, set_userLocation, onPickAndSort, t]);

    // Tote step: the scanned source tote SELECTS the wave, and it opens at the
    // anchored bay. An untouched claim left on another bay is reclaimed by the
    // BE; a wave with real sorting progress is refused, naming the bay.
    const handle_tote_scan = useCallback(async (scannedValue?: string) => {
        const val = (scannedValue ?? barcodeScan).trim();
        set_barcodeScan("");
        if (!val || !pasBay || !onPickAndSort) return;
        try {
            const res = await apiFetch(`/pick-and-sort/enter`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ barcode: pasBay, tote_barcode: val }),
            });
            const data = await res.json();
            if (res.ok && data.ok) {
                flashScanStatus("success");
                playSuccess();
                onPickAndSort(data as PickAndSortSession);
                return;
            }
            flashScanStatus("error");
            playError();
            toast.dismiss();
            if (!res.ok) { toast.error(t("scan_failed")); return; }
            const bays = Array.isArray(data.bay_names) ? data.bay_names.filter(Boolean).join(", ")
                : Array.isArray(data.occupied_bays) ? data.occupied_bays.join(", ") : "";
            toast.error(
                data.reason === "tote_not_awaiting_sort" ? t("pas_tote_no_wave")
                    // The tote's wave is queued for the OTHER wall.
                    : data.reason === "station_mismatch" ? t(data.wave_station === "put_wall" ? "pas_station_mismatch_put_wall" : "pas_station_mismatch_pas")
                    : data.reason === "tote_wave_in_progress" ? `${t("pas_tote_wave_in_progress")}${bays ? ` (${bays})` : ""}`
                        : data.reason === "bay_wave_in_progress" ? t("pas_bay_wave_in_progress")
                            : data.reason === "large_bays_not_empty" ? `${t("pas_large_bays_not_empty")}${bays ? ` (${bays})` : ""}`
                                : data.reason === "large_wrong_bay" ? t("pas_large_wrong_bay")
                                    : data.reason === "large_bays_unavailable" ? t("pas_large_bays_unavailable")
                                        : data.reason === "insufficient_locations" ? t("pas_insufficient_locations")
                                            : data.reason === "nothing_to_sort" ? t("pas_nothing_to_sort")
                                                : t("invalid_barcode"),
            );
        } catch {
            flashScanStatus("error");
            playError();
            toast.dismiss(); toast.error(t("scan_failed"));
        }
    }, [barcodeScan, pasBay, onPickAndSort, t]);

    return (
        <div className="scan-panel" style={{ backgroundColor: scanStatus === "success" ? "rgba(34, 197, 94, 0.18)" : undefined }}>
            <ScanFlashOverlay active={scanStatus === "error"} />
            <div className="page-title-bar">
                <h1 className="page-title">{isPembroke ? t("pick_and_sort") : t("zone_storage")}</h1>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {isPembroke && (
                        // The loader/unloader toggle, shown before a wave is loaded.
                        // Pre-wave both sides show the same generic scan prompt;
                        // scanning a bay slot loads the wave.
                        <div className="picking-tier-toggle" role="radiogroup" aria-label={t("pick_and_sort")}>
                            <button
                                type="button" role="radio" aria-checked={view === "loading"}
                                className={`picking-tier-pill pas-tier-pill${view === "loading" ? " picking-tier-pill--active" : ""}`}
                                onClick={() => set_view("loading")}
                                title={t("pas_view_loading")} aria-label={t("pas_view_loading")}
                            >
                                <ArrowDownToLine size={16} strokeWidth={2.2} aria-hidden="true" />
                                {sortCount !== null && sortCount.items > 0 && <span className="pas-tier-badge">{sortCount.items}</span>}
                            </button>
                            <button
                                type="button" role="radio" aria-checked={view === "unloading"}
                                className={`picking-tier-pill pas-tier-pill${view === "unloading" ? " picking-tier-pill--active" : ""}`}
                                onClick={() => set_view("unloading")}
                                title={t("pas_view_unloading")} aria-label={t("pas_view_unloading")}
                            >
                                <PackageOpen size={16} strokeWidth={2.2} aria-hidden="true" />
                                {sortCount !== null && sortCount.totes > 0 && <span className="pas-tier-badge">{sortCount.totes}</span>}
                            </button>
                        </div>
                    )}
                    <button className="page-back-btn" onClick={() => pasBay ? set_pasBay(null) : navigate("/")}><ArrowLeft size={20} /></button>
                </div>
            </div>
            <div className="scan-centre">
                <ScanStatusIcons status={scanStatus === "error" ? "idle" : scanStatus} />
                <div className={`scan-command ${scanStatus !== "idle" ? "scan-command-hidden" : ""}`}>
                    {(pasBay ? t("pas_scan_source_tote") : t("scan_location_barcode")).toUpperCase().split(" ").map((word, i) => (
                        <FitWord key={i}>{word}</FitWord>
                    ))}
                </div>
                {/* Unloading pill: which bay has orders ready to close, so the
                    closer picks a bay before they scan into it. */}
                {showBayReady && (
                    <div className="pas-bay-ready-gate">
                        <PasBayReadyTable bays={bayReady} currentBayIds={noBayHere} />
                    </div>
                )}
                {/* Tote step: the queue of waves awaiting sort. Scanning any listed
                    tote opens ITS wave at this bay — a wave already being sorted at
                    another bay is badged so the operator knows not to chase it. */}
                {pasBay && pasWaves.length > 0 && (
                    <div className="pas-wave-list">
                        <div className="pas-wave-list-title">{t(pasStation === "put_wall" ? "pas_available_waves_put_wall" : "pas_available_waves_pas")}</div>
                        {pasWaves.map(w => (
                            <div key={w.wave_id} className="pas-wave-row">
                                <span className="pas-wave-orders">{t("pas_wave_orders", { count: String(w.order_count) })}</span>
                                <span className="pas-wave-meta">{w.completed_at ? new Date(w.completed_at).toLocaleString([], { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}</span>
                                <span className="pas-wave-meta">{w.picker_name || "—"}</span>
                                {w.in_progress && <span className="pas-wave-busy">{t("pas_wave_in_progress")}</span>}
                                {typeof w.item_count === "number" && (
                                    <div className="pas-wave-sub">
                                        <span className="pas-wave-items">{t("pas_wave_items", { count: String(w.item_count) })}</span>
                                        {(w.totes?.length ?? 0) > 0 && (
                                            <span className="pas-wave-totes">{t("pas_wave_totes", { totes: (w.totes ?? []).join(", ") })}</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
            <HiddenScanInput value={barcodeScan} onChange={set_barcodeScan} onSubmit={pasBay ? handle_tote_scan : handle_location_barcode_scan} disabled={false} disableCamera />

            {demo && !pasBay && demoBays.length > 0 && (
                <div className="demo-scan-panel">
                    <span className="demo-scan-label">Demo · Sort bay</span>
                    {demoBays.map(b => (
                        <button key={b} className="demo-scan-btn" onClick={() => handle_location_barcode_scan(b)}>{b}</button>
                    ))}
                </div>
            )}
            {demo && pasBay && (
                <div className="demo-scan-panel">
                    <span className="demo-scan-label">Demo · Source tote</span>
                    {pasWaves.filter(w => !w.in_progress).flatMap(w => w.totes ?? []).slice(0, 6).map(tote => (
                        <button key={tote} className="demo-scan-btn" onClick={() => handle_tote_scan(tote)}>{tote}</button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ZoneStorageSelectLocation;
